(() => {
  'use strict';

  if (window.__KOFRA_CART_DRAWER_V32__) return;
  window.__KOFRA_CART_DRAWER_V32__ = true;


  const ROOT = window.Shopify?.routes?.root || '/';

  const rootUrl = (path) =>
    `${ROOT}${String(path || '').replace(/^\/+/, '')}`;

  const clamp = (value, min, max) =>
    Math.min(Math.max(Number(value) || 0, min), max);

  const pathOf = (url) => {
    try {
      return new URL(url, window.location.href).pathname.replace(/\/+$/, '');
    } catch (_) {
      return '';
    }
  };

  const CART_PATH = pathOf(rootUrl('cart'));

  /* Coincide con /cart/add, /cart/add.js y con prefijos de idioma (/en/cart/add). */
  const isCartAddAction = (action) =>
    /\/cart\/add(\.js)?$/.test(pathOf(action || ''));

  const safeImageUrl = (image) => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || '';
  };

  const resizeImage = (url, width = 300) => {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (_) {
      return url;
    }
  };


  class KofraCartDrawer extends HTMLElement {

    constructor() {
      super();

      this.sectionId = this.dataset.sectionId || '';
      this.enabled = this.dataset.enabled === 'true';

      this.minimumEnabled = this.dataset.minimumEnabled === 'true';
      this.minimumLock = this.dataset.minimumLock === 'true';
      this.minimumAmount = Number(this.dataset.minimumAmount || 0);

      this.freeShippingEnabled = this.dataset.freeShippingEnabled === 'true';
      this.freeShippingAmount = Number(this.dataset.freeShippingAmount || 0);
      this.discountTiersEnabled = this.dataset.discountTiersEnabled === 'true';

      this.recommendationsEnabled = this.dataset.recommendationsEnabled === 'true';
      this.recommendationsIntent = this.dataset.recommendationsIntent || 'complementary';
      this.recommendationsLimit = clamp(
        Number(this.dataset.recommendationsLimit || 6),
        1,
        10
      );

      this.currency =
        this.dataset.currency ||
        window.Shopify?.currency?.active ||
        'COP';

      this.config = window.KofraCartDrawerConfig?.[this.sectionId] || {};

      this.milestones = this.buildMilestones();

      /*
       * /cart.js no trae el "precio de comparación" (compare at price).
       * Lo pedimos a /products/<handle>.js y lo guardamos por variante.
       */
      this.compareAtCache = new Map();
      this.productFetches = new Map();

      this.renderedMilestonesKey = '';

      this.cart = null;
      this.isOpen = false;
      this.isBusy = false;
      this.checkoutBusy = false;
      this.lastFocusedElement = null;
      this.recommendationRequestId = 0;
      this.whatsAppObserver = null;
      this.badgeObserver = null;
      this.resizeFrame = null;

      this.boundDocumentClick = this.handleDocumentClick.bind(this);
      this.boundKeydown = this.handleKeydown.bind(this);
      this.boundCartMutation = this.handleExternalCartMutation.bind(this);
      this.boundResize = this.handleResize.bind(this);
      this.boundProductSubmit = this.handleProductFormSubmit.bind(this);
      this.boundOutsidePointer = this.handleOutsidePointer.bind(this);
    }


    connectedCallback() {
      if (this.dataset.kofraInitialized === 'true') return;
      this.dataset.kofraInitialized = 'true';

      this.cacheElements();
      this.applyThemeSettings();
      this.bindEvents();
      this.initWhatsAppObserver();
      this.initForeignBadgeGuard();

      this.refreshCart({ recommendations: false, silent: true }).catch(() => {});
    }


    disconnectedCallback() {
      document.removeEventListener('click', this.boundDocumentClick);
      document.removeEventListener('keydown', this.boundKeydown);
      document.removeEventListener('kofra:cart-mutated', this.boundCartMutation);
      window.removeEventListener('resize', this.boundResize);
      document.removeEventListener('submit', this.boundProductSubmit, true);
      document.removeEventListener('pointerdown', this.boundOutsidePointer, true);

      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);

      this.whatsAppObserver?.disconnect();
      this.badgeObserver?.disconnect();
      this.setWhatsAppVisibility(true);
    }


    cacheElements() {
      const q = (selector) => this.querySelector(selector);

      this.overlay = q('[data-kofra-cart-overlay]');
      this.panel = q('[data-kofra-cart-panel]');
      this.closeButton = q('[data-kofra-cart-close]');
      this.continueButton = q('[data-kofra-cart-continue]');
      this.itemsContainer = q('[data-kofra-cart-items]');
      this.emptyState = q('[data-kofra-cart-empty]');
      this.footer = q('[data-kofra-cart-footer]');
      this.countElement = q('[data-kofra-drawer-count]');
      this.subtotalElement = q('[data-kofra-cart-subtotal]');
      this.loadingElement = q('[data-kofra-cart-loading]');
      this.errorElement = q('[data-kofra-cart-error]');
      this.checkoutButton = q('[data-kofra-checkout]');
      this.checkoutLabel = q('[data-kofra-checkout-label]');
      this.checkoutLockIcon = q('[data-kofra-checkout-lock]');

      /* MINIMUM */
      this.minimumContainer = q('[data-kofra-minimum]');
      this.minimumMessage = q('[data-kofra-minimum-message]');
      this.minimumTrack = q('.kofra-cart-drawer__minimum-track');

      /* FREE SHIPPING */
      this.freeShippingContainer = q('[data-kofra-free-shipping]');
      this.freeShippingMessage = q('[data-kofra-free-shipping-message]');
      this.freeShippingTrack = q('.kofra-cart-drawer__reward-track');
      this.freeShippingTruck = q('[data-kofra-free-shipping-truck]');
      this.rewardUnlocked = q('[data-kofra-reward-unlocked]');
      this.rewardMilestones = q('[data-kofra-reward-milestones]');

      /* DISCOUNT ROW */
      this.discountRow = q('[data-kofra-cart-discount-row]');
      this.discountElement = q('[data-kofra-cart-discount]');

      /* RECOMMENDATIONS */
      this.desktopRecommendations = q('[data-kofra-desktop-recommendations]');
      this.desktopRecommendationsList = q('[data-kofra-desktop-recommendations-list]');
      this.mobileRecommendations = q('[data-kofra-mobile-recommendations]');
      this.mobileRecommendationsList = q('[data-kofra-mobile-recommendations-list]');

      /* TEMPLATES */
      this.itemTemplate = q('[data-kofra-cart-item-template]');
      this.recommendationTemplate = q('[data-kofra-recommendation-template]');
    }


    applyThemeSettings() {
      const drawerWidth = Number(this.dataset.drawerWidth || 460);
      const recommendationsWidth = Number(this.dataset.recommendationsWidth || 300);

      this.style.setProperty('--kofra-drawer-width', `${drawerWidth}px`);
      this.style.setProperty('--kofra-recommendations-width', `${recommendationsWidth}px`);

      if (this.dataset.drawerBackground) {
        this.style.setProperty('--kofra-background', this.dataset.drawerBackground);
      }

      if (this.dataset.drawerTextColor) {
        this.style.setProperty('--kofra-text', this.dataset.drawerTextColor);
      }

      if (this.dataset.accentColor) {
        this.style.setProperty('--kofra-accent', this.dataset.accentColor);
      }

      if (this.dataset.successColor) {
        this.style.setProperty('--kofra-success', this.dataset.successColor);
      }

      const opacity = clamp(Number(this.dataset.overlayOpacity || 45), 0, 100) / 100;

      if (this.overlay) {
        this.overlay.style.background = `rgba(0, 0, 0, ${opacity})`;
      }
    }


    bindEvents() {
      /*
       * El listener vive en document, así funciona para el carrito
       * del header, el mobile y el flotante, siempre que tengan
       * data-kofra-cart-link.
       */
      document.addEventListener('click', this.boundDocumentClick);
      document.addEventListener('keydown', this.boundKeydown);
      document.addEventListener('kofra:cart-mutated', this.boundCartMutation);
      window.addEventListener('resize', this.boundResize, { passive: true });

      /*
       * Fase de captura: interceptamos el "Agregar al carrito" antes que
       * el JS del tema, que es el que redirige a /cart.
       */
      document.addEventListener('submit', this.boundProductSubmit, true);

      /*
       * Cerrar al tocar fuera del drawer. Se escucha en document (y no solo
       * en el overlay) porque algunos temas ponen el header u otros
       * elementos por encima del overlay.
       */
      document.addEventListener('pointerdown', this.boundOutsidePointer, true);

      this.overlay?.addEventListener('click', () => this.close());
      this.closeButton?.addEventListener('click', () => this.close());
      this.continueButton?.addEventListener('click', () => this.close());
      this.checkoutButton?.addEventListener('click', () => this.handleCheckout());
    }


    handleDocumentClick(event) {
      if (!this.enabled) return;

      let trigger = event.target.closest('[data-kofra-cart-link]');

      /*
       * Cualquier link a /cart del tema (icono del header, etc.) abre el
       * drawer. La única forma de llegar a /cart es "Ver carrito completo"
       * o un link con data-kofra-skip.
       */
      if (!trigger) {
        const link = event.target.closest('a[href]');

        if (
          link &&
          !link.closest('.kofra-cart-drawer__view-cart, [data-kofra-skip]') &&
          !link.target &&
          pathOf(link.getAttribute('href')) === CART_PATH &&
          new URL(link.href, window.location.href).origin === window.location.origin
        ) {
          trigger = link;
        }
      }

      if (!trigger) return;

      /* Ctrl/Cmd + clic o clic central: dejar que abra en otra pestaña. */
      if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;

      event.preventDefault();
      event.stopPropagation();

      /* Si ya está abierto, el icono del carrito funciona como toggle. */
      if (this.isOpen) {
        this.close();
        return;
      }

      this.open(trigger);
    }


    handleOutsidePointer(event) {
      if (!this.isOpen) return;

      /* Solo clic principal / toque. */
      if (event.button > 0) return;

      const target = event.target;
      const path = typeof event.composedPath === 'function'
        ? event.composedPath()
        : [];

      const isInside = (element) =>
        element &&
        !element.hidden &&
        (path.includes(element) || element.contains(target));

      if (isInside(this.panel) || isInside(this.desktopRecommendations)) return;

      /* El icono del carrito lo maneja handleDocumentClick (toggle). */
      if (target instanceof Element && target.closest('[data-kofra-cart-link]')) return;

      this.close();

      /*
       * Al cerrar, el overlay deja de bloquear clics de inmediato. Nos
       * tragamos el clic que sigue a este toque para que no active
       * lo que haya debajo (un producto, un link del menú, etc.).
       */
      const swallowClick = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
      };

      document.addEventListener('click', swallowClick, { capture: true, once: true });

      setTimeout(() => {
        document.removeEventListener('click', swallowClick, true);
      }, 450);
    }


    handleProductFormSubmit(event) {
      if (!this.enabled) return;

      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;
      if (form.hasAttribute('data-kofra-skip')) return;
      if (!isCartAddAction(form.getAttribute('action'))) return;

      const submitter = event.submitter || null;

      /* Botones que van directo al checkout se respetan. */
      if (submitter && submitter.name === 'checkout') return;

      event.preventDefault();
      event.stopImmediatePropagation();

      this.addFromForm(form, submitter);
    }


    async addFromForm(form, submitter) {
      if (form.dataset.kofraAdding === 'true') return;

      form.dataset.kofraAdding = 'true';

      const button =
        submitter ||
        form.querySelector('[type="submit"], button:not([type])');

      const formData = new FormData(form);

      if (submitter?.name) {
        formData.append(submitter.name, submitter.value);
      }

      button?.classList.add('loading', 'kofra-is-adding');
      button?.setAttribute('aria-disabled', 'true');

      let addError = null;

      try {
        const response = await fetch(rootUrl('cart/add.js'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data.description ||
            data.message ||
            'No fue posible agregar el producto.'
          );
        }

        document.dispatchEvent(
          new CustomEvent('kofra:product-added', { detail: data })
        );

      } catch (error) {
        addError = error;

      } finally {
        form.dataset.kofraAdding = 'false';
        button?.classList.remove('loading', 'kofra-is-adding');
        button?.removeAttribute('aria-disabled');
      }

      /* Abre el drawer ya con el carrito actualizado (y el error si lo hubo). */
      await this.open(button || form);

      if (addError) {
        this.showError(addError.message);
      }
    }


    async open(trigger) {
      if (!this.enabled) {
        window.location.href = rootUrl('cart');
        return;
      }

      this.lastFocusedElement = trigger || document.activeElement;
      this.isOpen = true;

      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');

      document.documentElement.classList.add('kofra-cart-drawer-open');
      document.body.classList.add('kofra-cart-drawer-open');

      if (document.body.dataset.kofraCartPreviousOverflow === undefined) {
        document.body.dataset.kofraCartPreviousOverflow =
          document.body.style.overflow || '';
      }

      document.body.style.overflow = 'hidden';

      this.setWhatsAppVisibility(false);
      this.hideError();

      try {
        await this.refreshCart({ recommendations: true });
      } catch (error) {
        console.error('[KOFRA CART]', error);
        this.showError(this.config.errorText || 'No pudimos cargar el carrito.');
      }

      requestAnimationFrame(() => {
        this.panel?.focus({ preventScroll: true });
      });
    }


    close() {
      if (!this.isOpen) return;

      this.isOpen = false;

      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');

      document.documentElement.classList.remove('kofra-cart-drawer-open');
      document.body.classList.remove('kofra-cart-drawer-open');

      document.body.style.overflow =
        document.body.dataset.kofraCartPreviousOverflow || '';

      delete document.body.dataset.kofraCartPreviousOverflow;

      this.setWhatsAppVisibility(true);

      this.lastFocusedElement?.focus?.({ preventScroll: true });
    }


    handleKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') this.close();
    }


    handleResize() {
      if (!this.isOpen) return;

      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);

      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.fitDesktopRecommendations();
      });
    }


    initWhatsAppObserver() {
      if (!document.body) return;

      this.whatsAppObserver = new MutationObserver(() => {
        if (this.isOpen) this.setWhatsAppVisibility(false);
      });

      this.whatsAppObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }


    /*
     * Apps de etiquetas (Samita Product Labels) detectan las imágenes y links
     * de producto del drawer y les inyectan badges ("10% OFF", "Precio
     * sugerido"), que rompen el layout. Todo lo que inyecten aquí dentro
     * se elimina. Si envolvieron un elemento nuestro, lo sacamos primero.
     */
    initForeignBadgeGuard() {
      const FOREIGN_SELECTOR = '[class*="samita"], [data-samita], [id*="samita"]';

      const clean = (root) => {
        if (!(root instanceof Element)) return;

        const nodes = [];

        if (root.matches(FOREIGN_SELECTOR)) nodes.push(root);
        root.querySelectorAll(FOREIGN_SELECTOR).forEach((node) => nodes.push(node));

        nodes.forEach((node) => {
          if (!node.isConnected || !this.contains(node)) return;

          const ownChildren = Array.from(node.children).filter((child) =>
            Array.from(child.classList).some((name) => name.startsWith('kofra'))
          );

          if (ownChildren.length) {
            node.replaceWith(...ownChildren);
          } else {
            node.remove();
          }
        });
      };

      clean(this);

      this.badgeObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => clean(node));
        });
      });

      this.badgeObserver.observe(this, { childList: true, subtree: true });
    }


    setWhatsAppVisibility(visible) {
      document.querySelectorAll('.gty-wacb-button').forEach((button) => {
        if (visible) {
          button.style.removeProperty('display');
          button.style.removeProperty('visibility');
          button.style.removeProperty('opacity');
        } else {
          button.style.setProperty('display', 'none', 'important');
          button.style.setProperty('visibility', 'hidden', 'important');
          button.style.setProperty('opacity', '0', 'important');
        }
      });
    }


    /* ======================================================================
       API DEL CARRITO
       ====================================================================== */

    async getCart() {
      const response = await fetch(rootUrl('cart.js'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        throw new Error('No fue posible cargar el carrito.');
      }

      return response.json();
    }


    async changeLine(key, quantity) {
      const response = await fetch(rootUrl('cart/change.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: Math.max(0, Number(quantity))
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.description ||
          data.message ||
          'No fue posible actualizar el carrito.'
        );
      }

      return data;
    }


    async addVariant(variantId) {
      const response = await fetch(rootUrl('cart/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }]
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.description ||
          data.message ||
          'No fue posible agregar el producto.'
        );
      }

      return data;
    }


    async refreshCart({ recommendations = true, silent = false } = {}) {
      if (!silent) this.setLoading(true);

      try {
        const cart = await this.getCart();

        this.cart = cart;
        this.currency = cart.currency || this.currency || 'COP';

        this.renderCart(cart);
        this.syncHeaderCount(cart.item_count);

        if (recommendations && this.recommendationsEnabled && cart.item_count > 0) {
          await this.loadRecommendations(cart);
        } else if (cart.item_count <= 0) {
          this.hideRecommendations();
        }

        return cart;

      } finally {
        if (!silent) this.setLoading(false);
      }
    }


    /* ======================================================================
       RENDER
       ====================================================================== */

    renderCart(cart) {
      this.renderItems(cart);
      this.renderSubtotal(cart);
      this.renderCount(cart);
      this.renderMinimum(cart);
      this.renderFreeShipping(cart);
      this.renderCheckout(cart);
      this.renderEmptyState(cart);

      this.hydrateComparePrices(cart);
    }


    renderItems(cart) {
      if (!this.itemsContainer || !this.itemTemplate) return;

      this.itemsContainer.innerHTML = '';

      (cart.items || []).forEach((item) => {
        const fragment = this.itemTemplate.content.cloneNode(true);
        const q = (selector) => fragment.querySelector(selector);

        const article = q('.kofra-cart-item');
        const imageLink = q('[data-item-url]');
        const image = q('[data-item-image]');
        const title = q('[data-item-title]');
        const variant = q('[data-item-variant]');
        const properties = q('[data-item-properties]');
        const remove = q('[data-item-remove]');
        const minus = q('[data-item-minus]');
        const plus = q('[data-item-plus]');
        const quantity = q('[data-item-quantity]');
        const price = q('[data-item-price]');
        const comparePrice = q('[data-item-compare-price]');

        if (!article) return;

        article.dataset.lineKey = item.key;

        const itemUrl = item.url || '#';
        const itemTitle = item.product_title || item.title || 'Producto';

        if (imageLink) imageLink.href = itemUrl;

        if (title) {
          title.href = itemUrl;
          title.textContent = itemTitle;
        }

        if (image) {
          const imageUrl = safeImageUrl(item.image || item.featured_image);

          if (imageUrl) {
            image.src = resizeImage(imageUrl, 220);
            image.alt = itemTitle;
          } else {
            image.hidden = true;
          }
        }

        if (variant && item.variant_title && item.variant_title !== 'Default Title') {
          variant.textContent = item.variant_title;
          variant.hidden = false;
        }

        if (properties && item.properties && typeof item.properties === 'object') {
          const propertyRows = [];

          Object.entries(item.properties).forEach(([name, value]) => {
            if (!value || String(name).startsWith('_')) return;
            propertyRows.push(`${name}: ${value}`);
          });

          if (propertyRows.length) {
            properties.textContent = propertyRows.join(' · ');
            properties.hidden = false;
          }
        }

        if (quantity) {
          const currentQuantity = Number(item.quantity || 1);

          quantity.value = String(currentQuantity);

          /* Al tocar el campo se selecciona el número para reemplazarlo de una. */
          quantity.addEventListener('focus', () => quantity.select());

          quantity.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              quantity.blur();
            }
          });

          /* "change" se dispara al salir del campo o con Enter. */
          quantity.addEventListener('change', () => {
            const raw = String(quantity.value || '').trim();
            const next = Math.floor(Number(raw));

            if (raw === '' || !Number.isFinite(next) || next < 0) {
              quantity.value = String(currentQuantity);
              return;
            }

            if (next === currentQuantity) {
              quantity.value = String(currentQuantity);
              return;
            }

            this.updateItemQuantity(article, item.key, next);
          });
        }

        this.setLinePrices(article, item);

        minus?.addEventListener('click', () => {
          this.updateItemQuantity(article, item.key, item.quantity - 1);
        });

        plus?.addEventListener('click', () => {
          this.updateItemQuantity(article, item.key, item.quantity + 1);
        });

        remove?.addEventListener('click', () => {
          this.updateItemQuantity(article, item.key, 0);
        });

        this.itemsContainer.appendChild(fragment);
      });
    }


    async updateItemQuantity(article, key, quantity) {
      if (this.isBusy) {
        /* Si hay otra operación en curso, devolvemos el campo a su valor real. */
        if (this.cart) this.renderItems(this.cart);
        return;
      }

      this.isBusy = true;
      article?.classList.add('is-loading');
      this.hideError();

      try {
        await this.changeLine(key, quantity);

        /*
         * La respuesta de /cart/change.js no trae completos los descuentos de
         * pedido (cart_level_discount_applications), y sin ellos el 10% no se
         * puede repartir por línea. Pedimos /cart.js para tener el carrito
         * completo y consistente.
         */
        const cart = await this.getCart();

        this.cart = cart;
        this.renderCart(cart);
        this.syncHeaderCount(cart.item_count);

        if (cart.item_count > 0 && this.recommendationsEnabled) {
          await this.loadRecommendations(cart);
        } else {
          this.hideRecommendations();
        }

      } catch (error) {
        /*
         * Por ejemplo: pidieron 40 y solo hay 25 en stock. Recargamos el
         * carrito para que el campo muestre la cantidad real.
         */
        try {
          await this.refreshCart({ recommendations: false, silent: true });
        } catch (_) {}

        this.showError(
          error.message ||
          this.config.quantityErrorText ||
          'No fue posible actualizar esa cantidad.'
        );

      } finally {
        this.isBusy = false;
        article?.classList.remove('is-loading');
      }
    }


    /*
     * Precio tachado de la línea: el mayor entre
     * - el precio de comparación del producto × cantidad (producto en oferta)
     * - el precio original de la línea (si hay un descuento automático al producto)
     * Se muestra solo si es mayor que lo que realmente se paga.
     */
    setLinePrices(article, item) {
      if (!article) return;

      const price = article.querySelector('[data-item-price]');
      const comparePrice = article.querySelector('[data-item-compare-price]');

      const quantity = Number(item.quantity || 1);
      const originalPrice = Number(item.original_line_price ?? item.line_price ?? 0);
      /*
       * Lo que realmente se paga por la línea: el precio final de /cart.js
       * (ya incluye descuentos de producto) menos su parte de los descuentos
       * de pedido que no son niveles de volumen.
       */
      const reportedFinal = Number(item.final_line_price ?? item.line_price ?? originalPrice);
      const finalPrice = Math.max(0, reportedFinal - this.lineOrderDiscount(item));

      const compareUnit = Number(this.compareAtCache.get(Number(item.variant_id)) || 0);
      const compareLine = Math.max(originalPrice, compareUnit * quantity);

      if (price) {
        price.textContent = this.formatMoney(finalPrice);
        price.classList.toggle('is-discounted', compareLine > finalPrice);
      }

      if (comparePrice) {
        const show = compareLine > finalPrice;

        comparePrice.hidden = !show;
        comparePrice.textContent = show ? this.formatMoney(compareLine) : '';
      }
    }


    fetchProductCompareAt(handle) {
      if (!this.productFetches.has(handle)) {
        const request = fetch(rootUrl(`products/${encodeURIComponent(handle)}.js`), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        })
          .then((response) => (response.ok ? response.json() : null))
          .then((product) => {
            (product?.variants || []).forEach((variant) => {
              this.compareAtCache.set(
                Number(variant.id),
                Number(variant.compare_at_price || 0)
              );
            });
          })
          .catch(() => {});

        this.productFetches.set(handle, request);
      }

      return this.productFetches.get(handle);
    }


    async hydrateComparePrices(cart) {
      const handles = new Set();

      (cart.items || []).forEach((item) => {
        if (!item.handle) return;
        if (this.compareAtCache.has(Number(item.variant_id))) return;
        if (this.productFetches.has(item.handle)) return;

        handles.add(item.handle);
      });

      if (!handles.size) return;

      await Promise.all(
        Array.from(handles).map((handle) => this.fetchProductCompareAt(handle))
      );

      /* Actualiza las líneas que siguen en pantalla, sin re-renderizar todo. */
      const current = this.cart || cart;

      (current.items || []).forEach((item) => {
        const article = Array.from(
          this.itemsContainer?.querySelectorAll('.kofra-cart-item') || []
        ).find((element) => element.dataset.lineKey === item.key);

        this.setLinePrices(article, item);
      });
    }


    /*
     * Un descuento de pedido es de "volumen" (3%, 5%, 8%) si es un porcentaje
     * que coincide con uno de los niveles configurados. Esos se quedan en la
     * fila "Descuentos". Los demás descuentos de pedido (por ejemplo
     * "Aniversario Mayorista 10% Off") se reparten en cada línea y se
     * muestran tachados, igual que en las tarjetas de producto.
     */
    isVolumeTierApplication(application) {
      const tierPercents = new Set(
        (this.config.discountTiers || [])
          .map((tier) => Number(tier?.percent || 0))
          .filter((percent) => percent > 0)
      );

      return (
        application?.value_type === 'percentage' &&
        tierPercents.has(Number(application.value))
      );
    }


    /* Descuentos de pedido activos que se muestran por línea. */
    lineLevelOrderApplications(cart = this.cart) {
      return (cart?.cart_level_discount_applications || []).filter(
        (application) =>
          !this.isVolumeTierApplication(application) &&
          Number(application.total_allocated_amount || 0) > 0
      );
    }


    /*
     * Parte de esos descuentos que le corresponde a una línea, en proporción
     * a su precio. Para un porcentaje (como el 10%) el resultado es exacto.
     */
    lineOrderDiscount(item, cart = this.cart) {
      if (!cart) return 0;

      const totalDiscount = this.lineLevelOrderApplications(cart).reduce(
        (sum, application) => sum + Number(application.total_allocated_amount || 0),
        0
      );

      if (totalDiscount <= 0) return 0;

      const base = (cart.items || []).reduce(
        (sum, line) => sum + Number(line.final_line_price ?? line.line_price ?? 0),
        0
      );

      if (base <= 0) return 0;

      const lineBase = Number(item.final_line_price ?? item.line_price ?? 0);

      return Math.round((totalDiscount * lineBase) / base);
    }


    /* Porcentaje de esos descuentos, para mostrarlo en las recomendaciones. */
    storewidePercent(cart = this.cart) {
      return this.lineLevelOrderApplications(cart)
        .filter((application) => application.value_type === 'percentage')
        .reduce((sum, application) => sum + Number(application.value || 0), 0);
    }


    renderSubtotal(cart) {
      if (this.subtotalElement) {
        this.subtotalElement.textContent = this.formatMoney(cart.total_price);
      }

      /*
       * La fila "Descuentos" muestra solo lo que NO se ve ya en cada línea
       * (por ejemplo los niveles de 3%, 5% y 8%). Los descuentos de producto
       * ya aparecen tachados en su línea.
       */
      const productDiscounts = (cart.items || []).reduce((sum, item) => {
        const original = Number(item.original_line_price ?? item.line_price ?? 0);
        const reportedFinal = Number(item.final_line_price ?? original);

        return (
          sum +
          Math.max(0, original - reportedFinal) +
          this.lineOrderDiscount(item, cart)
        );
      }, 0);

      const discount = Math.max(0, Number(cart.total_discount || 0) - productDiscounts);

      if (this.discountRow) {
        this.discountRow.hidden = discount <= 0;
      }

      if (this.discountElement && discount > 0) {
        this.discountElement.textContent = `−${this.formatMoney(discount)}`;
      }
    }


    /*
     * Monto con el que se miden las metas: el total ANTES de descuentos.
     * Si usáramos el total con descuento, al desbloquear el 3% el carrito
     * bajaría de la meta y la barra "retrocedería".
     */
    goalSubtotal(cart) {
      return Number(
        cart.original_total_price ??
        cart.items_subtotal_price ??
        cart.total_price ??
        0
      );
    }


    renderCount(cart) {
      if (!this.countElement) return;

      const count = Number(cart.item_count || 0);

      this.countElement.textContent = String(count);
      this.countElement.hidden = count <= 0;
    }


    renderEmptyState(cart) {
      const empty = Number(cart.item_count || 0) <= 0;

      if (this.emptyState) this.emptyState.hidden = !empty;
      if (this.itemsContainer) this.itemsContainer.hidden = empty;
      if (this.footer) this.footer.hidden = empty;

      if (this.freeShippingContainer) {
        this.freeShippingContainer.hidden = empty || !this.milestones.length;
      }

      if (empty) this.hideRecommendations();
    }


    paintProgress(track, percentage, complete) {
      if (!track) return;

      const fillColor = complete
        ? (this.dataset.successColor || '#8B1E3F')
        : (this.dataset.accentColor || '#FE7F9C');

      track.style.setProperty(
        'background',
        `linear-gradient(to right, ${fillColor} 0%, ${fillColor} ${percentage}%, #f0dce1 ${percentage}%, #f0dce1 100%)`,
        'important'
      );
    }


    renderMinimum(cart) {
      if (!this.minimumEnabled || !this.minimumContainer) return;

      const subtotal = this.goalSubtotal(cart);
      const target = Number(this.minimumAmount || 0);
      const complete = target <= 0 || subtotal >= target;
      const progress = target > 0 ? clamp(subtotal / target, 0, 1) : 1;
      const percentage = clamp(progress * 100, 0, 100);
      const remaining = Math.max(target - subtotal, 0);

      this.minimumContainer.classList.toggle('is-complete', complete);
      this.paintProgress(this.minimumTrack, percentage, complete);

      if (this.minimumMessage) {
        this.minimumMessage.textContent = complete
          ? (this.config.minimumCompleteText || '✓ Ya puedes finalizar tu pedido')
          : this.replaceAmount(
              this.config.minimumPendingText ||
              'Te faltan {{ amount }} para completar el pedido mínimo',
              remaining
            );
      }
    }


    /*
     * Metas de la barra: envío gratis + niveles de descuento, ordenadas
     * por monto. Si una meta de descuento coincide con la de envío gratis
     * (por ejemplo 3% y envío en 500.000) se juntan en un solo punto.
     */
    buildMilestones() {
      const byAmount = new Map();

      const getMilestone = (amount) => {
        if (!byAmount.has(amount)) {
          byAmount.set(amount, { amount, shipping: false, discount: 0 });
        }
        return byAmount.get(amount);
      };

      if (this.freeShippingEnabled && this.freeShippingAmount > 0) {
        getMilestone(this.freeShippingAmount).shipping = true;
      }

      if (this.discountTiersEnabled) {
        (this.config.discountTiers || []).forEach((tier) => {
          const percent = Number(tier?.percent || 0);
          const amount = Number(tier?.amount || 0);

          if (percent <= 0 || amount <= 0) return;

          const milestone = getMilestone(amount);
          milestone.discount = Math.max(milestone.discount, percent);
        });
      }

      return Array.from(byAmount.values()).sort((a, b) => a.amount - b.amount);
    }


    milestoneLabel(milestone) {
      const parts = [];

      if (milestone.shipping) {
        parts.push(this.config.freeShippingLabel || 'Envío gratis');
      }

      if (milestone.discount) {
        parts.push(`${milestone.discount}%`);
      }

      return parts.join(' + ');
    }


    renderMilestoneMarkers(unlockedCount) {
      if (!this.rewardMilestones) return;

      const total = this.milestones.length;
      const useMarkers = total > 1;

      this.freeShippingContainer?.classList.toggle('has-milestones', useMarkers);

      if (!useMarkers) {
        this.rewardMilestones.innerHTML = '';
        this.renderedMilestonesKey = '';
        return;
      }

      const key = this.milestones.map((m) => m.amount).join('|');

      if (this.renderedMilestonesKey !== key) {
        this.rewardMilestones.innerHTML = '';

        this.milestones.forEach((milestone, index) => {
          const marker = document.createElement('span');
          marker.className = 'kofra-reward-milestone';
          marker.classList.toggle('is-last', index === total - 1);
          marker.style.left = `${((index + 1) / total) * 100}%`;

          const dot = document.createElement('span');
          dot.className = 'kofra-reward-milestone__dot';

          const label = document.createElement('span');
          label.className = 'kofra-reward-milestone__label';
          label.textContent = this.milestoneLabel(milestone);

          marker.append(dot, label);
          this.rewardMilestones.appendChild(marker);
        });

        this.renderedMilestonesKey = key;
      }

      Array.from(this.rewardMilestones.children).forEach((marker, index) => {
        marker.classList.toggle('is-unlocked', index < unlockedCount);
      });
    }


    renderFreeShipping(cart) {
      if (!this.freeShippingContainer || !this.milestones.length) return;

      const subtotal = this.goalSubtotal(cart);
      const milestones = this.milestones;
      const total = milestones.length;

      const unlocked = milestones.filter((m) => subtotal >= m.amount);
      const unlockedCount = unlocked.length;
      const next = milestones[unlockedCount] || null;
      const complete = !next;

      /*
       * Cada meta ocupa el mismo tramo de la barra (1/total), así
       * 500.000, 1.500.000 y 3.000.000 quedan bien repartidos aunque
       * los montos sean muy distintos.
       */
      let progress = 1;

      if (next) {
        const previousAmount = unlockedCount > 0 ? milestones[unlockedCount - 1].amount : 0;
        const segment = next.amount - previousAmount;
        const inside = segment > 0 ? clamp((subtotal - previousAmount) / segment, 0, 1) : 0;

        progress = (unlockedCount + inside) / total;
      }

      const percentage = clamp(progress * 100, 0, 100);

      this.freeShippingContainer.classList.toggle('is-complete', complete);
      this.paintProgress(this.freeShippingTrack, percentage, complete);
      this.renderMilestoneMarkers(unlockedCount);

      if (this.freeShippingTruck) {
        this.freeShippingTruck.style.setProperty(
          'left',
          `${clamp(percentage, 3, 97)}%`,
          'important'
        );
      }

      const bestDiscount = unlocked.reduce((max, m) => Math.max(max, m.discount), 0);
      const hasShipping = unlocked.some((m) => m.shipping);

      /* Mensaje principal */
      if (this.freeShippingMessage) {
        let message;

        if (complete) {
          message = bestDiscount > 0
            ? this.replaceTokens(
                this.config.rewardsCompleteText ||
                '🎉 ¡Desbloqueaste ENVÍO GRATIS y {{ discount }}% de descuento!',
                { discount: bestDiscount }
              )
            : (this.config.freeShippingCompleteText || '🎉 ¡Ya tienes ENVÍO GRATIS!');

        } else {
          const remaining = Math.max(next.amount - subtotal, 0);

          if (next.shipping) {
            message = this.replaceAmount(
              this.config.freeShippingPendingText ||
              'Te faltan {{ amount }} para ENVÍO GRATIS',
              remaining
            );

            if (next.discount) {
              message += ` + ${next.discount}% OFF`;
            }

          } else {
            message = this.replaceTokens(
              this.config.discountPendingText ||
              'Te faltan {{ amount }} para {{ discount }}% de DESCUENTO',
              { amount: remaining, discount: next.discount }
            );
          }
        }

        this.freeShippingMessage.textContent = message;
      }

      /* Línea secundaria con lo ya desbloqueado */
      if (this.rewardUnlocked) {
        const parts = [];

        if (hasShipping) {
          parts.push((this.config.freeShippingLabel || 'Envío gratis').toLowerCase());
        }

        if (bestDiscount > 0) {
          parts.push(`${bestDiscount}% de descuento`);
        }

        const show = !complete && parts.length > 0;

        this.rewardUnlocked.hidden = !show;

        if (show) {
          this.rewardUnlocked.textContent =
            `${this.config.rewardsUnlockedPrefix || '✓ Ya tienes'} ${parts.join(' y ')}`;
        }
      }
    }


    isCheckoutLocked(subtotal) {
      return (
        this.minimumEnabled &&
        this.minimumLock &&
        this.minimumAmount > 0 &&
        Number(subtotal || 0) < this.minimumAmount
      );
    }


    renderCheckout(cart) {
      if (!this.checkoutButton) return;

      const locked = this.isCheckoutLocked(this.goalSubtotal(cart));

      this.checkoutButton.classList.toggle('is-locked', locked);
      this.checkoutButton.setAttribute('aria-disabled', String(locked));

      if (this.checkoutLockIcon) {
        this.checkoutLockIcon.hidden = !locked;
      }

      if (this.checkoutLabel) {
        this.checkoutLabel.textContent = locked
          ? (this.config.checkoutLockedText || 'Completa el pedido mínimo')
          : (this.config.checkoutText || 'Finalizar pedido');
      }
    }


    async handleCheckout() {
      if (this.checkoutBusy || this.isBusy) return;

      this.checkoutBusy = true;
      this.checkoutButton?.classList.add('is-loading');
      this.hideError();

      try {
        const cart = await this.getCart();

        this.cart = cart;
        this.renderCart(cart);

        if (Number(cart.item_count || 0) <= 0) {
          throw new Error('Tu carrito está vacío.');
        }

        if (this.isCheckoutLocked(this.goalSubtotal(cart))) {
          const remaining = this.minimumAmount - this.goalSubtotal(cart);

          this.showError(
            this.replaceAmount(
              this.config.minimumPendingText ||
              'Te faltan {{ amount }} para completar el pedido mínimo',
              remaining
            )
          );

          return;
        }

        window.location.assign(rootUrl('checkout'));

      } catch (error) {
        this.showError(
          error.message ||
          this.config.errorText ||
          'No fue posible continuar al checkout.'
        );

      } finally {
        this.checkoutBusy = false;
        this.checkoutButton?.classList.remove('is-loading');
      }
    }


    /* ======================================================================
       RECOMENDACIONES
       ====================================================================== */

    async loadRecommendations(cart) {
      if (!this.recommendationsEnabled || !cart.items?.length) {
        this.hideRecommendations();
        return;
      }

      const referenceItem = cart.items[cart.items.length - 1];
      const productId = referenceItem.product_id;

      if (!productId) {
        this.hideRecommendations();
        return;
      }

      const requestId = ++this.recommendationRequestId;

      try {
        let products = await this.fetchRecommendations(
          productId,
          this.recommendationsIntent
        );

        if (this.recommendationsIntent === 'complementary' && !products.length) {
          products = await this.fetchRecommendations(productId, 'related');
        }

        if (requestId !== this.recommendationRequestId) return;

        const productsInCart = new Set(
          cart.items.map((item) => Number(item.product_id))
        );

        const filtered = products
          .filter((product) =>
            product &&
            product.available !== false &&
            !productsInCart.has(Number(product.id))
          )
          .slice(0, this.recommendationsLimit);

        if (!filtered.length) {
          this.hideRecommendations();
          return;
        }

        this.renderRecommendations(filtered);

      } catch (error) {
        console.warn('[KOFRA RECOMMENDATIONS]', error);
        this.hideRecommendations();
      }
    }


    async fetchRecommendations(productId, intent) {
      const params = new URLSearchParams({
        product_id: String(productId),
        /* Pedimos el máximo (10) para que sobren productos después de filtrar los que ya están en el carrito. */
        limit: '10',
        intent: String(intent)
      });

      const response = await fetch(
        `${rootUrl('recommendations/products.json')}?${params.toString()}`,
        {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        }
      );

      if (!response.ok) {
        throw new Error('No fue posible cargar recomendaciones.');
      }

      const data = await response.json();

      return Array.isArray(data.products) ? data.products : [];
    }


    renderRecommendations(products) {
      if (!this.recommendationTemplate) return;

      if (this.desktopRecommendationsList) {
        this.desktopRecommendationsList.innerHTML = '';
      }

      if (this.mobileRecommendationsList) {
        this.mobileRecommendationsList.innerHTML = '';
      }

      products.forEach((product) => {
        this.desktopRecommendationsList?.appendChild(
          this.createRecommendationCard(product)
        );

        this.mobileRecommendationsList?.appendChild(
          this.createRecommendationCard(product)
        );
      });

      if (this.desktopRecommendations) {
        this.desktopRecommendations.hidden = products.length <= 0;
      }

      if (this.mobileRecommendations) {
        this.mobileRecommendations.hidden = products.length <= 0;
      }

      this.fitDesktopRecommendations();
    }


    /*
     * Muestra en la columna desktop solo las tarjetas que caben
     * completas en la altura disponible. En pantallas bajas salen 2,
     * en pantallas altas 4, 5 o más (hasta el máximo configurado).
     */
    fitDesktopRecommendations() {
      const aside = this.desktopRecommendations;
      const list = this.desktopRecommendationsList;

      if (!aside || !list || aside.hidden) return;

      const cards = Array.from(list.children);
      if (!cards.length) return;

      cards.forEach((card) => { card.hidden = false; });

      /* En mobile y tablet la columna está oculta por CSS. */
      if (window.getComputedStyle(aside).display === 'none') return;

      const styles = window.getComputedStyle(list);
      const gap = parseFloat(styles.rowGap) || 0;
      const paddingY =
        (parseFloat(styles.paddingTop) || 0) +
        (parseFloat(styles.paddingBottom) || 0);

      const available = list.clientHeight - paddingY;
      const cardHeight = cards[0].getBoundingClientRect().height;

      if (available <= 0 || cardHeight <= 0) return;

      const fits = Math.max(
        1,
        Math.floor((available + gap) / (cardHeight + gap))
      );

      cards.forEach((card, index) => {
        card.hidden = index >= fits;
      });
    }


    createRecommendationCard(product) {
      const fragment = this.recommendationTemplate.content.cloneNode(true);
      const q = (selector) => fragment.querySelector(selector);

      const card = q('.kofra-cart-recommendation');
      const imageLink = q('[data-rec-url]');
      const image = q('[data-rec-image]');
      const title = q('[data-rec-title]');
      const price = q('[data-rec-price]');
      const button = q('[data-rec-add]');

      const productUrl =
        product.url ||
        (product.handle ? rootUrl(`products/${product.handle}`) : '#');

      if (imageLink) imageLink.href = productUrl;

      if (title) {
        title.href = productUrl;
        title.textContent = product.title || 'Producto';
      }

      if (image) {
        const imageUrl = safeImageUrl(product.featured_image);

        if (imageUrl) {
          image.src = resizeImage(imageUrl, 360);
          image.alt = product.title || '';
        } else {
          image.hidden = true;
        }
      }

      const variants = Array.isArray(product.variants) ? product.variants : [];
      const availableVariant = variants.find((variant) => variant.available);

      if (!availableVariant) {
        if (button) {
          button.disabled = true;
          button.textContent = 'Agotado';
        }

        if (price) {
          price.textContent = this.formatMoney(product.price || 0);
        }

        return fragment;
      }

      if (price) {
        const basePrice = Number(availableVariant.price ?? product.price ?? 0);
        const percent = clamp(this.storewidePercent(), 0, 100);

        /* Si hay un descuento general activo (como el 10%), también aplica aquí. */
        const current = Math.round(basePrice * (1 - percent / 100));
        const compare = Math.max(
          Number(availableVariant.compare_at_price ?? product.compare_at_price ?? 0),
          percent > 0 ? basePrice : 0
        );

        price.textContent = '';

        if (compare > current) {
          const compareElement = document.createElement('s');
          compareElement.className = 'kofra-cart-recommendation__compare-price';
          compareElement.textContent = this.formatMoney(compare);
          price.appendChild(compareElement);
        }

        const currentElement = document.createElement('span');
        currentElement.textContent = this.formatMoney(current);
        price.appendChild(currentElement);
      }

      if (button) {
        button.textContent = this.config.recommendationButtonText || '+ Agregar';

        button.addEventListener('click', () => {
          this.addRecommendation(card, button, availableVariant.id);
        });
      }

      return fragment;
    }


    async addRecommendation(card, button, variantId) {
      if (this.isBusy) return;

      this.isBusy = true;
      card?.classList.add('is-loading');
      button.disabled = true;
      this.hideError();

      const originalText = button.textContent;

      try {
        await this.addVariant(variantId);

        button.textContent = this.config.recommendationAddedText || 'Agregado ✓';

        const cart = await this.getCart();

        this.cart = cart;
        this.renderCart(cart);
        this.syncHeaderCount(cart.item_count);

        await this.loadRecommendations(cart);

      } catch (error) {
        button.disabled = false;
        button.textContent = originalText;
        card?.classList.remove('is-loading');

        this.showError(error.message || 'No fue posible agregar el producto.');

      } finally {
        this.isBusy = false;
      }
    }


    hideRecommendations() {
      if (this.desktopRecommendations) this.desktopRecommendations.hidden = true;
      if (this.mobileRecommendations) this.mobileRecommendations.hidden = true;
      if (this.desktopRecommendationsList) this.desktopRecommendationsList.innerHTML = '';
      if (this.mobileRecommendationsList) this.mobileRecommendationsList.innerHTML = '';
    }


    /* ======================================================================
       SINCRONIZACIÓN EXTERNA
       ====================================================================== */

    async handleExternalCartMutation() {
      /* Evita refrescos duplicados mientras este drawer modifica el carrito. */
      if (this.isBusy || this.checkoutBusy) return;

      this.hideError();

      try {
        await this.refreshCart({
          recommendations: this.isOpen,
          silent: true
        });
      } catch (_) {}
    }


    syncHeaderCount(count) {
      const safe = Math.max(0, Number(count || 0));

      /* Actualiza contador del header, mobile y botón flotante. */
      document.querySelectorAll('[data-kofra-cart-count]').forEach((element) => {
        element.textContent = String(safe);
        element.toggleAttribute('hidden', safe <= 0);
      });
    }


    /* ======================================================================
       HELPERS
       ====================================================================== */

    formatMoney(cents) {
      const value = Number(cents || 0) / 100;

      try {
        return new Intl.NumberFormat('es-CO', {
          style: 'currency',
          currency: this.currency || 'COP',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value);
      } catch (_) {
        return '$' + Math.round(value).toLocaleString('es-CO');
      }
    }


    replaceTokens(text, { amount, discount } = {}) {
      let result = String(text || '');

      if (amount !== undefined) {
        result = result.replace(/\{\{\s*amount\s*\}\}/g, this.formatMoney(amount));
      }

      if (discount !== undefined) {
        result = result.replace(/\{\{\s*discount\s*\}\}/g, String(discount));
      }

      return result;
    }


    replaceAmount(text, cents) {
      return String(text || '').replace(
        /\{\{\s*amount\s*\}\}/g,
        this.formatMoney(cents)
      );
    }


    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = String(message || '');
      this.errorElement.hidden = false;
    }


    hideError() {
      if (!this.errorElement) return;
      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }


    setLoading(state) {
      if (!this.loadingElement) return;
      this.loadingElement.hidden = !state;
      this.loadingElement.setAttribute('aria-hidden', String(!state));
    }
  }


  if (!customElements.get('kofra-cart-drawer')) {
    customElements.define('kofra-cart-drawer', KofraCartDrawer);
  }

})();